import { useEffect, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { ErrorBoundary } from './ErrorBoundary';
import { BelPemberitahuan } from './BelPemberitahuan';
import { BottomNav } from './BottomNav';
import { HubTabs } from './HubTabs';
import { Sidebar } from './Sidebar';
import { useSession } from './WorkforceContext';
import { fetchMemberships, fetchOrgRoster } from '../api/hbs';
import { displayNameOf, headerLine } from '../lib/display-name';
import { navHubs } from '../lib/nav-hub';
import { visibleNavItems } from '../lib/navigation';
import { isOversightRole, roleLabel, type WorkforceRole } from '../lib/worker-roles';

/**
 * Kerangka aplikasi lapangan — mobile-first.
 *
 * Navigasi disaring menurut `permissions` dari `/api/me`. Itu hanya menyembunyikan jalan buntu;
 * penegakan tetap di API, dan setiap halaman tetap aman bila URL-nya dipaksa dibuka.
 */
export function AppShell(): ReactNode {
  const session = useSession();
  const { me } = session;
  const oversight = isOversightRole(me.roles as readonly WorkforceRole[]);
  // Menu bergrup (#1758): satu grup per entri menu, layar di dalamnya jadi tab (`HubTabs`).
  const hubs = navHubs(visibleNavItems(me.permissions), oversight);

  const roleText =
    me.roles.length > 0 ? me.roles.map((r) => roleLabel(r as WorkforceRole)).join(', ') : null;

  /*
   * Nama organisasi dan nama pemakai — KEDUANYA milik HBS, bukan Workforce (sama seperti sapaan
   * beranda di `HomePage`). `MeResponse` hanya membawa `organizationId` (UUID); nama organisasi
   * sebelumnya hanya pernah diambil SEKALI, di layar masuk, lalu dibuang (item 12, review 12 Sep
   * 2026). Kegagalannya SENGAJA diam — header tanpa nama org/nama pemakai tetap header yang utuh
   * (lihat `headerLine`), sedangkan kartu galat di kepala SETIAP layar adalah gangguan yang tidak
   * sebanding.
   */
  const [namaOrg, setNamaOrg] = useState<string | null>(null);
  const [namaSaya, setNamaSaya] = useState<string | null>(null);
  useEffect(() => {
    let aktif = true;
    fetchMemberships(session.auth.accessToken)
      .then((daftar) => {
        const cocok = daftar.find((m) => m.organizationId === me.organizationId);
        if (aktif && cocok) setNamaOrg(cocok.organizationName);
      })
      .catch(() => {
        /* diam — lihat alasan di atas */
      });
    fetchOrgRoster(session.auth.accessToken, me.organizationId)
      .then((roster) => {
        if (aktif) setNamaSaya(displayNameOf(roster, me.userId));
      })
      .catch(() => {
        /* diam — lihat alasan di atas */
      });
    return () => {
      aktif = false;
    };
  }, [session.auth.accessToken, me.organizationId, me.userId]);

  return (
    /*
     * `.shell-oversight` (item 7, review 12 Sep 2026) HANYA dipasang untuk Owner/Manajer. Kurir/
     * Karyawan menerima `<div className="shell">` yang PERSIS SAMA dengan sebelum PR ini — bukan
     * "sidebar disembunyikan lewat CSS", tapi kelas layout desktop itu sendiri tidak pernah ada
     * di DOM mereka. "Kurir tetap kolom sempit" berarti kolom sempitnya tidak boleh bergantung
     * pada satu aturan CSS yang kebetulan tidak menyala — ia harus tidak bisa menyala sama sekali.
     */
    <div className={oversight ? 'shell shell-oversight' : 'shell'}>
      <header className="shell-header">
        {/*
          SATU baris: nama org · nama pemakai · peran (item 12, review 12 Sep 2026). Sebelumnya
          dua baris ("HBS Workforce" lalu peran saja) tidak menjawab pertanyaan yang sebenarnya
          ditanyakan orang yang mengelola banyak organisasi: "saya sedang masuk sebagai apa, di
          organisasi mana, dan sebagai siapa?" — nama aplikasi tidak menjawab satu pun itu.
        */}
        <h1 className="shell-header-title">{headerLine([namaOrg, namaSaya, roleText])}</h1>
        <div className="header-aksi">
          <BelPemberitahuan />
        </div>
      </header>

      {/*
        Sidebar HANYA dirender bila `oversight` — bukan dirender selalu lalu disembunyikan CSS
        untuk semua orang. React tidak pernah memasang node-nya ke DOM Kurir/Karyawan sama sekali.
      */}
      {oversight ? <Sidebar hubs={hubs} /> : null}

      <main className="shell-main">
        <HubTabs hubs={hubs} />
        <ErrorBoundary>
          <Outlet />
        </ErrorBoundary>
      </main>

      {/*
        Kaki halaman DIHAPUS (arah Owner 23 Agu 2026).

        Ia dulu berbunyi "Penegakan izin sepenuhnya di API. Tampilan ini hanya menyembunyikan yang
        tidak relevan." Itu catatan arsitektur — benar, dan ditujukan kepada pembaca kode, bukan
        kepada kurir yang membuka aplikasi ini sambil berdiri di jalan. Ia muncul di SETIAP layar,
        menjelaskan mesin yang tidak pernah ditanyakan pemakainya.

        Pernyataannya tetap hidup di tempat yang benar: komentar di puncak `navigation.ts`.
      */}

      {/*
        Bilah bawah TETAP dirender untuk Owner/Manajer juga (bukan hanya Kurir) — di bawah
        1024px, peran pengawas tetap memakainya persis seperti sebelum PR ini; `.shell-oversight`
        yang menyembunyikannya lewat CSS begitu layar cukup lebar untuk sidebar (item 7).
      */}
      <BottomNav hubs={hubs} />
    </div>
  );
}
